'use client';

import React, { useState } from 'react';

const signs = [
  { id: 1, label: 'SKY', arabic: 'السماء', name: 'AR-RAHMAN', desc: 'The sky held up without pillars — a sign of the Most Compassionate who sustains without visible support.', angle: -90 },
  { id: 2, label: 'BIRDS', arabic: 'الطير', name: 'AL-BASIR', desc: 'Quran 67:19 — Do they not see the birds above them spreading and folding their wings? None holds them up except Ar-Rahman. He is All-Seeing.', angle: -45 },
  { id: 3, label: 'WATER', arabic: 'الماء', name: 'AR-RAZZAQ', desc: 'Water descends from the sky as provision — a sign of the Ever-Provider whose sustenance reaches all.', angle: 0 },
  { id: 4, label: 'TREES', arabic: 'الشجر', name: 'AL-KHALIQ', desc: 'A tree says Subhanallah with every leaf. The Creator embedded praise in the structure of growth itself.', angle: 45 },
  { id: 5, label: 'EARTH', arabic: 'الأرض', name: 'AL-HAFIZ', desc: 'The earth holds what is placed upon it — a sign of the Preserver who maintains all things in their place.', angle: 90 },
  { id: 6, label: 'NIGHT', arabic: 'الليل', name: 'AL-MALIK', desc: 'The alternation of night and day — a sign of the Sovereign whose dominion encompasses all cycles.', angle: 135 },
  { id: 7, label: 'SELF', arabic: 'النفس', name: 'AL-KHABIR', desc: 'And in yourselves — do you not see? The self is a sign. The All-Aware placed signs within the one who seeks.', angle: 180 },
  { id: 8, label: 'FIRE', arabic: 'النار', name: 'AL-NUR', desc: 'Light from fire — a sign of the Light, whose light illuminates the heavens and the earth.', angle: 225 },
  { id: 9, label: 'WIND', arabic: 'الريح', name: 'AL-QADIR', desc: 'The wind moves what it will — a sign of the All-Powerful whose command none can resist.', angle: 270 },
  { id: 10, label: 'SEED', arabic: 'البذرة', name: 'AL-BARI', desc: 'From a seed, a tree. From nothing, everything. The Originator fashions forms from the formless.', angle: 315 },
];

export default function RadialSignsMap() {
  const [active, setActive] = useState<number | null>(null);
  const R = 200;

  const activeSign = signs?.find((s) => s?.id === active);

  return (
    <section className="py-24 interactive-section relative" id="signs-map" style={{ borderTop: '1px solid rgba(139,105,20,0.2)' }}>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>Interactive Map</p>
          <h2 className="font-mono text-3xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Signs in Creation</h2>
          <p className="font-mono text-xs mt-2" style={{ color: 'rgba(245,240,232,0.3)' }}>Click any sign to reveal its connection to a Name of Allah</p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-12">

          {/* SVG Radial Map */}
          <div className="relative flex-shrink-0" style={{ width: 500, height: 500 }}>
            <svg viewBox="-260 -260 520 520" width="500" height="500">
              {/* Rings */}
              {[60, 120, 180, 220]?.map((r) => (
                <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(139,105,20,0.12)" strokeWidth="1" />
              ))}

              {/* Radial lines */}
              {signs?.map((sign) => {
                const rad = (sign?.angle * Math.PI) / 180;
                return (
                  <line key={sign?.id}
                    x1={60 * Math.cos(rad)} y1={60 * Math.sin(rad)}
                    x2={R * Math.cos(rad)} y2={R * Math.sin(rad)}
                    stroke={active === sign?.id ? 'rgba(179,138,42,0.8)' : 'rgba(139,105,20,0.2)'}
                    strokeWidth={active === sign?.id ? '1.5' : '0.5'}
                    style={{ transition: 'all 0.3s ease' }}
                  />
                );
              })}

              {/* Sign nodes */}
              {signs?.map((sign) => {
                const rad = (sign?.angle * Math.PI) / 180;
                const x = R * Math.cos(rad);
                const y = R * Math.sin(rad);
                const isActive = active === sign?.id;
                return (
                  <g key={sign?.id} transform={`translate(${x}, ${y})`}
                    onClick={() => setActive(isActive ? null : sign?.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx="0" cy="0" r="18"
                      fill={isActive ? '#B38A2A' : '#1C2B25'}
                      stroke={isActive ? '#D4A82A' : 'rgba(139,105,20,0.4)'}
                      strokeWidth="1"
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    <text x="0" y="4" textAnchor="middle"
                      fill={isActive ? '#1C2B25' : 'rgba(245,240,232,0.8)'}
                      fontSize="6" fontFamily="monospace" fontWeight="900"
                      style={{ transition: 'all 0.3s ease' }}
                    >
                      {sign?.label}
                    </text>
                  </g>
                );
              })}

              {/* Center */}
              <circle cx="0" cy="0" r="55" fill="#1C2B25" stroke="rgba(179,138,42,0.5)" strokeWidth="1" />
              <circle cx="0" cy="0" r="45" fill="none" stroke="rgba(139,105,20,0.2)" strokeWidth="0.3" opacity="1" />
              <text x="0" y="-8" textAnchor="middle" fill="#F5F0E8" fontSize="12" fontFamily="monospace" fontWeight="900">ALLAH</text>
              <text x="0" y="8" textAnchor="middle" fill="rgba(179,138,42,0.6)" fontSize="6" fontFamily="monospace" letterSpacing="1">THE ORIGIN</text>
            </svg>
          </div>

          {/* Detail panel */}
          <div className="flex-1">
            {activeSign ? (
              <div className="p-8 transition-all duration-300" style={{ border: '1px solid rgba(179,138,42,0.3)', background: 'rgba(28,43,37,0.4)' }}>
                <div className="flex items-start gap-4 mb-6">
                  <span className="font-mono text-5xl" style={{ color: 'rgba(179,138,42,0.2)' }}>{activeSign?.arabic}</span>
                  <div>
                    <p className="font-mono text-xs tracking-widest uppercase mb-1" style={{ color: 'rgba(139,105,20,0.6)' }}>Sign</p>
                    <h3 className="font-mono text-2xl font-black uppercase" style={{ color: '#F5F0E8' }}>{activeSign?.label}</h3>
                    <span className="font-mono text-xs tracking-widest px-2 py-1 mt-2 inline-block" style={{ color: 'rgba(245,240,232,0.6)', border: '1px solid rgba(139,105,20,0.3)' }}>{activeSign?.name}</span>
                  </div>
                </div>
                <p className="font-mono text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.5)' }}>{activeSign?.desc}</p>
                <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(44,74,62,0.4)' }}>
                  <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.2)' }}>Every Ayah is a bridge, not a destination — it points back to the One who placed it.</p>
                </div>
              </div>
            ) : (
              <div className="p-8" style={{ border: '1px solid rgba(44,74,62,0.35)', background: 'rgba(28,43,37,0.3)' }}>
                <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.5)' }}>Select a Sign</p>
                <p className="font-mono text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>
                  Click any node on the radial map to reveal how that sign in creation connects to a Name of Allah — and back to the Haal Project framework.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-2">
                  {signs?.map((s) => (
                    <button key={s?.id} onClick={() => setActive(s?.id)}
                      className="font-mono text-xs text-left px-3 py-2 transition-colors"
                      style={{ color: 'rgba(245,240,232,0.35)', border: '1px solid rgba(44,74,62,0.3)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.3)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.35)'; }}
                    >
                      {s?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
